import React from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { ChildFeePayment } from "../../../types/parentFamily";
import { formatLedgerCurrency, formatLedgerDate } from "../../../utils/schoolFeesHelpers";
import {
  MANAGEMENT_ACTION_ICON_BUTTON_SIZE,
  MANAGEMENT_ACTION_ICON_FONT_SIZE,
  MANAGEMENT_ACTION_ROW_SX,
} from "../../../styles/managementCardActions";
import { lightTheme } from "../../../styles/theme";
import { FAMILY_LEDGER_PAYMENT_SX, FAMILY_TEXT_LINK_SX } from "./familyStyles";

interface CollapsiblePaymentsProps {
  sectionKey: string;
  payments: ChildFeePayment[];
  currency: string;
  expanded: boolean;
  onToggle: (sectionKey: string) => void;
  onEdit: (payment: ChildFeePayment) => void;
  onDelete: (payment: ChildFeePayment) => void;
}

export const CollapsiblePayments: React.FC<CollapsiblePaymentsProps> = ({
  sectionKey,
  payments,
  currency,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}) => {
  const sorted = [...payments].sort((a, b) => a.paymentNumber - b.paymentNumber);

  if (sorted.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        No payments recorded yet.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Button
        sx={{
          ...FAMILY_TEXT_LINK_SX,
          typography: "caption",
          fontWeight: 600,
        }}
        onClick={() => onToggle(sectionKey)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        }
      >
        {expanded ? "Hide payments" : `Show payments (${sorted.length})`}
      </Button>
      <Collapse in={expanded}>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {sorted.map((payment) => (
            <Paper key={payment.id} variant="outlined" sx={FAMILY_LEDGER_PAYMENT_SX}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500}>
                    Payment {payment.paymentNumber}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatLedgerDate(payment.paidDate)} · Receipt {payment.receiptNo}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">
                    {formatLedgerCurrency(payment.amountPaid, currency)}
                  </Typography>
                  <Box sx={MANAGEMENT_ACTION_ROW_SX}>
                    <IconButton
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      aria-label="Edit payment"
                      onClick={() => onEdit(payment)}
                      sx={{ color: lightTheme.colors.primary }}
                    >
                      <EditIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                    <IconButton
                      size={MANAGEMENT_ACTION_ICON_BUTTON_SIZE}
                      aria-label="Delete payment"
                      onClick={() => onDelete(payment)}
                      sx={{ color: lightTheme.colors.error }}
                    >
                      <DeleteIcon fontSize={MANAGEMENT_ACTION_ICON_FONT_SIZE} />
                    </IconButton>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
};
